import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
  Skeleton,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Banknote, CalendarDays, Clock } from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { SalaryAdvanceStatus } from "@/modules/hr/domain/types";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  useCancelSalaryAdvanceMutation,
  useSalaryAdvanceDetailQuery,
} from "../api/queries";

const STATUS_LABELS: Record<SalaryAdvanceStatus, string> = {
  pending: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
  fully_repaid: "ຫັກຄືນຄົບ",
};

function statusVariant(
  s: SalaryAdvanceStatus,
): "default" | "destructive" | "secondary" {
  if (s === "approved" || s === "paid" || s === "fully_repaid")
    return "default";
  if (s === "rejected" || s === "cancelled") return "destructive";
  return "secondary";
}

const REPAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "ລໍຖ້າຫັກ",
  deducted: "ຫັກແລ້ວ",
};

export function SalaryAdvanceDetailPage() {
  const { id } = useParams({ from: "/app/hr/salary-advance/requests/$id" });
  const navigate = useNavigate();
  const { data, isLoading } = useSalaryAdvanceDetailQuery(id ?? "");
  const cancelMutation = useCancelSalaryAdvanceMutation();
  const [showCancel, setShowCancel] = useState(false);

  const handleCancel = () => {
    cancelMutation.mutate(id ?? "", {
      onSuccess: () => {
        toast.success("ຍົກເລີກຄຳຂໍສຳເລັດ");
        setShowCancel(false);
      },
      onError: (err: { message?: string }) => {
        toast.error(err?.message || "ເກີດຂໍ້ຜິດພາດ");
      },
    });
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2">
              <Skeleton className="h-8 w-64 max-w-full" />
              <Skeleton className="h-4 w-48" />
            </div>
            <Skeleton className="h-9 w-28" />
          </div>
          <div className="mx-auto w-full max-w-2xl space-y-4">
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
        </Main>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">ບໍ່ພົບຄຳຂໍນີ້</p>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">
                ລາຍລະອຽດຄຳຂໍເບິກເງິນ
              </h1>
              <Badge variant={statusVariant(data.status)}>
                {STATUS_LABELS[data.status] ?? data.status}
              </Badge>
            </div>
            <p className="mt-2 text-muted-foreground text-sm">
              ຍື່ນວັນທີ {formatDateLocal(data.requestDate)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            {data.status === "pending" && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowCancel(true)}
              >
                ຍົກເລີກຄຳຂໍ
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                navigate({ to: "/app/hr/salary-advance/requests" })
              }
            >
              <ArrowLeft className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <div className="mx-auto w-full max-w-2xl space-y-6">
          {/* Main card */}
          <Card className="gap-0 overflow-hidden border-border/80 p-0 shadow-sm">
            <CardHeader className="space-y-1 border-border/60 border-b bg-muted/30 py-4">
              <CardTitle className="font-semibold text-lg">ສະຫຼຸບຄຳຂໍ</CardTitle>
              <CardDescription>
                ຈຳນວນ, ວັນທີ, ສະຖານະ, ແລະ ຂໍ້ມູນການອະນຸມັດ
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 p-0">
              {data.status === "rejected" && data.rejectionReason && (
                <div className="border-border/60 border-b bg-destructive/5 px-6 py-4">
                  <Alert
                    variant="destructive"
                    className="border-destructive/40 bg-transparent"
                  >
                    <AlertTitle>ຄຳຂໍຖືກປະຕິເສດ</AlertTitle>
                    <AlertDescription className="mt-1 text-pretty">
                      {data.rejectionReason}
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              <div className="px-6 pt-6">
                <div className="flex flex-col gap-4 rounded-xl border border-primary/15 bg-linear-to-br from-primary/5 via-muted/40 to-muted/20 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Banknote className="size-5" />
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                        ຈຳນວນທີ່ຂໍ
                      </p>
                      <p className="mt-0.5 font-semibold text-foreground text-xl">
                        {Number(data.amount).toLocaleString()} ກີບ
                      </p>
                      {data.approvedAmount != null && (
                        <p className="mt-0.5 text-muted-foreground text-sm">
                          ອະນຸມັດ:{" "}
                          <span className="font-medium text-foreground">
                            {Number(data.approvedAmount).toLocaleString()} ກີບ
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge
                    variant={statusVariant(data.status)}
                    className="px-2.5 py-0.5"
                  >
                    {STATUS_LABELS[data.status] ?? data.status}
                  </Badge>
                </div>
              </div>

              <Separator className="bg-border/60" />

              <dl className="grid gap-3 px-6 sm:grid-cols-2">
                <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-sm">
                    <CalendarDays className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      ວັນທີ່ຍື່ນ
                    </dt>
                    <dd className="mt-1 font-medium leading-snug">
                      {formatDateLocal(data.requestDate)}
                    </dd>
                  </div>
                </div>

                <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-sm">
                    <Clock className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      ຈຳນວນງວດຫັກຄືນ
                    </dt>
                    <dd className="mt-1 font-medium leading-snug">
                      {data.repaymentMonths} ງວດ
                    </dd>
                  </div>
                </div>

                {data.approvedAt && (
                  <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4 sm:col-span-2">
                    <div className="min-w-0">
                      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ການອະນຸມັດ
                      </dt>
                      <dd className="mt-1 font-medium text-sm leading-snug">
                        ອະນຸມັດເມື່ອ {formatDateTimeLocal(data.approvedAt)}
                      </dd>
                    </div>
                  </div>
                )}

                {data.paidAt && (
                  <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4 sm:col-span-2">
                    <div className="min-w-0">
                      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ວັນທີ່ຈ່າຍເງິນ
                      </dt>
                      <dd className="mt-1 font-medium text-sm leading-snug">
                        {formatDateTimeLocal(data.paidAt)}
                      </dd>
                    </div>
                  </div>
                )}
              </dl>

              {data.reason && (
                <>
                  <Separator className="bg-border/60" />
                  <div className="px-6 pb-2">
                    <div className="rounded-xl border border-border/60 bg-muted/25 p-4">
                      <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ເຫດຜົນ
                      </p>
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">
                        {data.reason}
                      </p>
                    </div>
                  </div>
                </>
              )}

              <div className="border-border/60 border-t bg-muted/20 px-6 py-3">
                <p className="text-center text-muted-foreground text-xs">
                  ສ້າງເມື່ອ {formatDateTimeLocal(data.createdAt)}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Repayment schedule */}
          {data.repayments.length > 0 && (
            <Card className="gap-0 overflow-hidden border-border/80 p-0 shadow-sm">
              <CardHeader className="border-border/60 border-b bg-muted/30 py-4">
                <CardTitle className="font-semibold text-base">
                  ຕາຕະລາງຫັກຄືນ
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-muted/20">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                        ງວດ
                      </th>
                      <th className="px-4 py-2 text-right font-medium text-muted-foreground">
                        ຈຳນວນ
                      </th>
                      <th className="px-4 py-2 text-right font-medium text-muted-foreground">
                        ສະຖານະ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.repayments.map((r) => (
                      <tr key={r.id} className="border-border/40 border-t">
                        <td className="px-4 py-2">
                          {r.periodMonth}/{r.periodYear}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {Number(r.amount).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Badge
                            variant={
                              r.status === "deducted" ? "default" : "secondary"
                            }
                            className="text-xs"
                          >
                            {REPAYMENT_STATUS_LABELS[r.status] ?? r.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-center pb-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() =>
                navigate({ to: "/app/hr/salary-advance/requests" })
              }
            >
              <ArrowLeft className="mr-1 size-4" />
              ກັບໄປລາຍການ
            </Button>
          </div>
        </div>

        {/* Cancel confirm */}
        {showCancel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-sm rounded-xl bg-background p-6 shadow-xl">
              <h2 className="font-semibold text-lg">ຢືນຢັນການຍົກເລີກ</h2>
              <p className="mt-2 text-muted-foreground text-sm">
                ທ່ານຕ້ອງການຍົກເລີກຄຳຂໍນີ້ແທ້ບໍ່? ການດຳເນີນການນີ້ບໍ່ສາມາດຍ້ອນກັບໄດ້
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCancel(false)}
                  disabled={cancelMutation.isPending}
                >
                  ກັບຄືນ
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleCancel}
                  disabled={cancelMutation.isPending}
                >
                  {cancelMutation.isPending ? "ກຳລັງດຳເນີນການ..." : "ຢືນຢັນຍົກເລີກ"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Main>
    </>
  );
}
